<script setup lang="ts">
/**
 * Orchestrates the 5 entity panels for a ticket.
 *
 * Delegates all entity state management to useTicketEntityState —
 * this component is purely about layout and event wiring.
 */
import { computed, ref, toRef } from "vue";
import { useTicketEntityState } from "src/composables/ticket-entities/useTicketEntityState";
import {
  createOrganisationSearchConfig,
  createUnitSearchConfig,
  createSiteSearchConfig,
  createContactPersonSearchConfig,
} from "src/composables/ticket-entities/configs";
import type {
  FetchOrganisationResponse,
  FetchUnitResponse,
  FetchSiteResponse,
  FetchOrganisationContactPersonResponse,
} from "src/api/models";
import type { BaseEntity } from "src/composables/ticket-entities/types";
import EntityPickerPanel from "src/components/shared/EntityPickerPanel.vue";
import TicketEditSiteCreate from "./TicketEditSiteCreate.vue";
import TicketEditContactCreate from "./TicketEditContactCreate.vue";
import { ENTITY_THEMES } from "./entity-display";

const props = defineProps<{
  ticketId?: string;
  disabled?: boolean;
}>();

const ticketIdRef = toRef(props, "ticketId");

const {
  organisation,
  unit,
  site,
  contactPerson,
  unitContactPerson,
  unitMismatch,
  siteMismatch,
  contactPersonMismatch,
  unitContactPersonMismatch,
  organisationId,
  isInitializing,
  setOrganisation,
  setUnit,
  setSite,
  setContactPerson,
  setUnitContactPerson,
  initializeFromTicket,
  reset,
} = useTicketEntityState({ ticketId: ticketIdRef });

const unitConfig = createUnitSearchConfig();
const orgConfig = createOrganisationSearchConfig();
const siteConfig = createSiteSearchConfig();
const contactConfig = createContactPersonSearchConfig();

const organisationName = computed(() => organisation.value?.name);

const unitRelatedOrgId = computed(() => {
  if (organisation.value) return undefined;
  return unit.value?.organisation?.id;
});
const unitName = computed(() => {
  if (!unit.value) return undefined;
  return unit.value.name || (unit.value.fleet_number ? `#${unit.value.fleet_number}` : undefined);
});

async function handleUnitSelect(entity: BaseEntity): Promise<void> {
  await setUnit(entity as FetchUnitResponse);
}

async function handleUnitClear(): Promise<void> {
  await setUnit(undefined);
}

async function handleOrgSelect(entity: BaseEntity): Promise<void> {
  await setOrganisation(entity as FetchOrganisationResponse);
}

async function handleOrgClear(): Promise<void> {
  await setOrganisation(undefined);
}

async function handleSiteSelect(entity: BaseEntity): Promise<void> {
  await setSite(entity as FetchSiteResponse);
}

async function handleSiteClear(): Promise<void> {
  await setSite(undefined);
}

async function handleContactSelect(entity: BaseEntity): Promise<void> {
  await setContactPerson(entity as FetchOrganisationContactPersonResponse);
}

async function handleContactClear(): Promise<void> {
  await setContactPerson(undefined);
}

async function handleSiteContactSelect(entity: BaseEntity): Promise<void> {
  await setUnitContactPerson(entity as FetchOrganisationContactPersonResponse);
}

async function handleSiteContactClear(): Promise<void> {
  await setUnitContactPerson(undefined);
}

const siteCreateMode = ref(false);
const contactCreateMode = ref(false);

async function handleSiteCreated(newSite: FetchSiteResponse): Promise<void> {
  await setSite(newSite);
  siteCreateMode.value = false;
}

async function handleContactCreated(newContact: Record<string, any>): Promise<void> {
  await setUnitContactPerson(newContact as FetchOrganisationContactPersonResponse);
  contactCreateMode.value = false;
}

function resetAll(): void {
  reset();
  siteCreateMode.value = false;
  contactCreateMode.value = false;
}

defineExpose({ initializeFromTicket, reset: resetAll, organisation, unit, site, contactPerson, unitContactPerson });
</script>

<template>
  <div class="flex flex-col gap-2.5">
    <div class="grid grid-cols-2 gap-2.5">
      <EntityPickerPanel
        label="Unit"
        :theme="ENTITY_THEMES.unit"
        :config="unitConfig"
        :model-value="unit"
        :parent-id="organisationId"
        :parent-name="organisationName"
        :mismatch="unitMismatch"
        :disabled="disabled"
        :loading="isInitializing"
        @select="handleUnitSelect"
        @clear="handleUnitClear"
      />

      <EntityPickerPanel
        label="Opdrachtgever"
        :theme="ENTITY_THEMES.organisation"
        :config="orgConfig"
        :model-value="organisation"
        :parent-id="unitRelatedOrgId"
        :parent-name="unitName"
        :disabled="disabled"
        :loading="isInitializing"
        @select="handleOrgSelect"
        @clear="handleOrgClear"
      />
    </div>
    <div class="grid grid-cols-2 gap-2.5">

      <TicketEditSiteCreate
        v-if="siteCreateMode"
        :initial-entity="site"
        @created="handleSiteCreated"
        @cancel="siteCreateMode = false"
      />
      <EntityPickerPanel
        v-else
        label="Locatie"
        :theme="ENTITY_THEMES.site"
        :config="siteConfig"
        :model-value="site"
        :parent-id="organisationId"
        :parent-name="organisationName"
        :mismatch="siteMismatch"
        :disabled="disabled"
        :loading="isInitializing"
        @select="handleSiteSelect"
        @clear="handleSiteClear"
      >
        <template #actions>
          <button class="new-btn" @click="siteCreateMode = true">+ New</button>
        </template>
      </EntityPickerPanel>
      <EntityPickerPanel
        label="Contact opdrachtgever"
        :theme="ENTITY_THEMES.orgContact"
        :config="contactConfig"
        :model-value="contactPerson"
        :parent-id="organisationId"
        :parent-name="organisationName"
        :mismatch="contactPersonMismatch"
        :disabled="disabled"
        :loading="isInitializing"
        @select="handleContactSelect"
        @clear="handleContactClear"
      />
    </div>

    <div class="grid grid-cols-2 gap-2.5">

      <TicketEditContactCreate
        v-if="contactCreateMode"
        :initial-entity="unitContactPerson"
        @created="handleContactCreated"
        @cancel="contactCreateMode = false"
      />
      <EntityPickerPanel
        v-else
        label="Contact locatie"
        :theme="ENTITY_THEMES.siteContact"
        :config="contactConfig"
        :model-value="unitContactPerson"
        :parent-id="organisationId"
        :parent-name="organisationName"
        :mismatch="unitContactPersonMismatch"
        :disabled="disabled"
        :loading="isInitializing"
        @select="handleSiteContactSelect"
        @clear="handleSiteContactClear"
      >
        <template #actions>
          <button class="new-btn" @click="contactCreateMode = true">+ New</button>
        </template>
      </EntityPickerPanel>
    </div>
  </div>
</template>

<style scoped>
.new-btn {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-primary-500);
  cursor: pointer;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  transition: all 0.15s;
}
.new-btn:hover {
  background: var(--p-primary-50);
}
:root[class*='app-dark'] .new-btn:hover {
  background: color-mix(in srgb, var(--p-primary-500) 10%, transparent);
}
</style>
